import {
  AccountTree,
  Add,
  Assessment,
  AutoAwesome,
  Build,
  Category,
  Check,
  Description,
  Extension,
  Folder,
  HomeRounded,
  LabelImportant,
  Logout,
  Payment,
  Person,
  PlaylistAdd,
  PlaylistAddCheck,
  Settings,
  StickyNote2,
  Upload,
  WarningAmber,
} from '@mui/icons-material';
import {
  alpha,
  Avatar,
  Box,
  BoxProps,
  CircularProgress,
  Drawer,
  DrawerProps,
  IconButton,
  ListItemAvatar,
  ListItemButton,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  Menu,
  Stack,
  Toolbar,
  Typography,
  useTheme,
} from '@mui/material';
import { useEntityActions } from 'api/entity';
import { useCurrentUser } from 'api/providers/authProvider';
import {
  useManageWorkspace,
  useWorkspace,
} from 'api/providers/workspaceProvider';
import { useCreateWorkspace } from 'components/createWorkspace';
import { AnimatePresence, motion } from 'framer-motion';
import useConfirm from 'hooks/useConfirm';
import { useIsTablet } from 'hooks/useIsMobile';
import { useDraggable } from 'providers/DnDProvider';
import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { FinsightEntity } from 'shared';
import { useSWRConfig } from 'swr';
import { formatName } from 'utils/fields';
import { useUserPermissions } from 'utils/user';

export default function Sidebar(props: DrawerProps) {
  const isMobile = useIsTablet();
  const workspace = useWorkspace();
  const { key: location } = useLocation();
  const user = useCurrentUser();

  const [workspaceMenuAnchorEl, setWorkspaceMenuAnchorEl] =
    useState<null | HTMLElement>(null);

  const lastLocation = useRef(location);

  useEffect(() => {
    if (lastLocation.current !== location) {
      lastLocation.current = location;
      if (isMobile) {
        props.onClose?.({}, 'backdropClick');
      }
    }
  }, [isMobile, location, props]);

  return (
    <>
      <Drawer
        {...props}
        variant={isMobile ? 'temporary' : 'permanent'}
        PaperProps={{
          sx: {
            backgroundImage: 'unset',
            minHeight: 0,
            position: 'unset',
            overflow: 'unset',
          },
        }}
        sx={{
          ...props.sx,
          height: '100%',
          '&, & .MuiPaper-root': {
            width: 300,
          },
        }}
      >
        <Toolbar />
        <Stack flexGrow={1} justifyContent="space-between" minHeight={0}>
          <Stack
            sx={{
              bgcolor: 'transparent',
              display: 'flex',
              flexDirection: 'column',
              overflowY: 'auto',
            }}
          >
            <LiHeader>For You</LiHeader>
            {/* <Li icon={<Inbox />} text="Inbox" /> */}
            <Li icon={<HomeRounded />} text="Home" exact to="/" />
            <Li disabled icon={<Assessment />} text="Reports" />
            <Li icon={<PlaylistAdd />} text="Coming Up" to="/coming-up" />
            <LiHeader>Entities</LiHeader>
            <EntitiesLi
              type="group"
              icon={<Folder />}
              text="Groups"
              to="/groups"
            />
            <EntitiesLi
              type="Items"
              icon={<LabelImportant />}
              text="Items"
              to="/items"
            />
            <Li icon={<Category />} text="Entities" to="/entities" />
            <Li icon={<Description />} text="File Thingy" to="/file-thingy" />

            <LiHeader>Project Management</LiHeader>
            <EntitiesLi
              type="project"
              icon={<AccountTree />}
              text="Projects"
              to="/projects"
            />
            <EntitiesLi
              type="task"
              icon={<PlaylistAddCheck />}
              text="Tasks"
              to="/tasks"
            />
            <LiHeader>Data Management</LiHeader>
            <Li icon={<AutoAwesome />} text="Data Maid" to="/datamaid" />
            <Li icon={<Settings />} text="Automations" to="/automations" />
            <Li icon={<Extension />} text="Plugins" to="/plugins" />
            <Li icon={<StickyNote2 />} text="Scratch Pad" to="/scratchpad" />
            <Li icon={<Upload />} text="Files" to="/files" />
            <LiHeader>Admin</LiHeader>
            <Li to="/field-editor" icon={<Build />} text="Field Editor" />
            {user?.subscription == 'root' && (
              <Li
                to="/json-generator"
                icon={<Build />}
                text="Field JSON Generator"
              />
            )}
            <LiHeader>Personal</LiHeader>
            <Li toplevel to="/account" icon={<Person />} text="My Account" />
            <Li to="/404" icon={<Payment />} text="My Plan & Billing" />
          </Stack>
          {workspace.data ? (
            <ListItemButton
              onClick={(e) => setWorkspaceMenuAnchorEl(e.currentTarget)}
              sx={{
                borderColor: 'divider',
                borderTopWidth: 1,
                borderTopStyle: 'solid',
                flexGrow: 0,
              }}
            >
              <ListItemAvatar>
                <Avatar>
                  {workspace.data.name
                    .split(' ')
                    .slice(0, 2)
                    .map((x) => x[0])
                    .join('')}
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={workspace.data.name}
                secondary={formatName(workspace.data.role)}
              />
              <ListItemSecondaryAction>
                <IconButton size="small">
                  <Logout fontSize="small" />
                </IconButton>
              </ListItemSecondaryAction>
            </ListItemButton>
          ) : (
            'No workspace'
          )}
        </Stack>
      </Drawer>
      <Menu
        anchorEl={workspaceMenuAnchorEl}
        open={Boolean(workspaceMenuAnchorEl)}
        onClose={() => setWorkspaceMenuAnchorEl(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <WorkspaceSwitcher onClose={() => setWorkspaceMenuAnchorEl(null)} />
      </Menu>
    </>
  );
}

function LiHeader(props: { children?: React.ReactNode }) {
  return (
    <Typography
      variant="h6"
      fontSize="0.9em"
      color="text.secondary"
      px={2}
      py={1}
    >
      {props.children}
    </Typography>
  );
}

function WorkspaceSwitcher({ onClose }: { onClose?: () => void }) {
  const { showCreateWorkspaceModal } = useCreateWorkspace();
  const userPermissions = useUserPermissions();
  const workspaceManager = useManageWorkspace();

  if (workspaceManager.workspaces.error) {
    return (
      <Stack py={1} px={2} direction="row" alignItems="center" gap={2}>
        <WarningAmber color="error" />
        <Typography>Error loading workspaces</Typography>
      </Stack>
    );
  }

  if (workspaceManager.workspaces.isLoading) {
    return (
      <Stack py={1} px={2} direction="row" alignItems="center" gap={2}>
        <CircularProgress size={32} />
        <Typography>Loading workspaces...</Typography>
      </Stack>
    );
  }

  const userWorkspaceCreatePermission = userPermissions.workspaces.create();

  return (
    <>
      {workspaceManager.workspaces.data?.map((workspace) => (
        <ListItemButton
          key={workspace.id}
          href={`/workspace/${workspace.id}`}
          onClick={() => {
            // workspaceManager.setCurrentWorkspace(workspace.id);
            onClose?.();
          }}
        >
          <ListItemAvatar>
            <Avatar>
              {workspace.name
                .split(' ')
                .slice(0, 2)
                .map((x) => x[0])
                .join('')}
            </Avatar>
          </ListItemAvatar>
          <ListItemText
            primary={workspace.name}
            secondary={formatName(workspace.role)}
          />
          <ListItemSecondaryAction>
            <IconButton>
              {workspace.id === workspaceManager.workspaceId && <Check />}
            </IconButton>
          </ListItemSecondaryAction>
        </ListItemButton>
      ))}
      <ListItemButton
        disabled={!userWorkspaceCreatePermission.allowed}
        onClick={() => {
          if (userWorkspaceCreatePermission.allowed) {
            onClose?.();
            showCreateWorkspaceModal();
          }
        }}
        sx={{
          minWidth: 350,
        }}
      >
        <ListItemIcon>
          <Add />
        </ListItemIcon>
        <ListItemText
          primary="Create new workspace"
          secondary={
            userWorkspaceCreatePermission.allowed
              ? ''
              : userWorkspaceCreatePermission.message
          }
        />
      </ListItemButton>
    </>
  );
}

function EntitiesLi(props: {
  type: string;
  icon: ReactNode;
  text: string;
  to: string;
}) {
  const { type, icon, text, to } = props;
  return (
    <Droppable
      alignTextLeft
      overlayText="Move to root"
      parentId={null}
      cachePurgeKeys={[`/entity/${type}/root`, `/entity/${type}/all`]}
    >
      <Li icon={icon} text={text} to={to} />
    </Droppable>
  );
}

type DroppableProps = {
  children?: React.ReactNode;
  boxProps?: BoxProps;
  alignTextLeft?: boolean;
  parentId?: string | null;
  cachePurgeKeys?: string[];
  overlayText?: string;
};

export function Droppable(props: DroppableProps) {
  const {
    children,
    boxProps,
    alignTextLeft,
    parentId,
    cachePurgeKeys,
    overlayText,
  } = props;

  const theme = useTheme();
  const { updateEntity } = useEntityActions();
  const draggableContext = useDraggable();
  const { cache, mutate } = useSWRConfig();
  const { confirm } = useConfirm();

  const [dropMode, setDropMode] = useState(false);

  useEffect(() => {
    if (!draggableContext.dragItem) {
      setDropMode(false);
    }
  }, [draggableContext.dragItem]);

  return (
    <Box
      {...boxProps}
      sx={{
        ...boxProps?.sx,
        display: 'grid',
        gridTemplateColumns: '1fr',
        gridTemplateRows: '1fr',
        '& > *': {
          gridColumn: 1,
          gridRow: 1,
        },
      }}
      onMouseOver={() => {
        if (
          draggableContext.dragItem &&
          draggableContext.dragItem.type === 'entity'
        ) {
          setDropMode(true);
        }
      }}
      onMouseLeave={() => {
        setDropMode(false);
      }}
      onMouseUp={async () => {
        if (
          parentId !== undefined &&
          dropMode &&
          draggableContext.dragItem?.type === 'entity'
        ) {
          const droppedEntity = draggableContext.dragItem
            .data as FinsightEntity;
          if (!(await confirm('Are you sure you want to move this entity?')))
            return;
          try {
            const response = await updateEntity(droppedEntity.id, {
              parent: parentId,
            });
            if (!response.ok) throw new Error();

            const finalCachePurgeKeys = cachePurgeKeys ?? [];

            if (droppedEntity.parent) {
              finalCachePurgeKeys.push(
                `/entity/${droppedEntity.parent}/children`,
              );
            }

            if (finalCachePurgeKeys?.length) {
              Array.from(cache.keys())
                .filter((x) =>
                  finalCachePurgeKeys?.some((key) => x.includes(key)),
                )
                .forEach((key) => mutate(key));
            }
          } catch (e) {
            console.log("ERRORRR during drag and drop, couldn't move");
          }
        }
      }}
    >
      {children}
      <AnimatePresence>
        {dropMode && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            style={{
              zIndex: 1,
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              padding: '8px 16px',
              justifyContent: alignTextLeft ? 'flex-start' : 'center',
              borderRadius: theme.shape.borderRadius,
              border: '1px dashed',
              borderColor: theme.palette.divider,
              background: alpha(theme.palette.background.paper, 0.1),
              backdropFilter: 'blur(5px) saturate(2)',
              overflow: 'hidden',
              cursor: 'pointer',
            }}
          >
            <motion.div
              initial={{
                top: -50,
                opacity: 0,
              }}
              animate={{
                top: 0,
                opacity: 1,
              }}
              style={{
                position: 'relative',
                pointerEvents: 'none',
              }}
            >
              <Typography>
                {overlayText ?? 'Drop here to move into this entity'}
              </Typography>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </Box>
  );
}

function Li(props: {
  icon?: React.ReactNode;
  to?: string;
  text: string;
  disabled?: boolean;
  toplevel?: boolean;
  exact?: boolean;
}) {
  const toplevel = props.toplevel ?? false;
  const { pathname } = useLocation();
  const workspace = useWorkspace();

  const pathnameWithoutWorkspace = pathname.replace(
    `/workspace/${workspace.data?.id}`,
    '',
  );

  return (
    <ListItemButton
      dense
      href={
        props.to
          ? toplevel
            ? `${props.to}?workspace=${workspace.data?.id}`
            : workspace
              ? `/workspace/${workspace.data?.id}${props.to}`
              : ''
          : ''
      }
      disabled={props.disabled}
      selected={
        props.to
          ? props.exact
            ? pathnameWithoutWorkspace === props.to
            : pathnameWithoutWorkspace.startsWith(props.to)
          : false
      }
    >
      {props.icon && <ListItemIcon>{props.icon}</ListItemIcon>}
      <ListItemText primary={props.text} />
    </ListItemButton>
  );
}
